// Pure lookup for the wizard's resolution-plan min-payment value, sourced from
// the BE-supplied `min_payment_by_method` grid. Returns null for any state
// where the lookup isn't applicable yet (different plan type, missing
// frequency/method selection, missing map entry); the wizard falls through to
// its FE-side `calculateMinPayment` in that case.
//
// Extracted as a pure function so it can be unit-tested without spinning up
// React + Redux + react-hook-form. The wizard hook is the only caller.

/**
 * @param {object} args
 * @param {string|false} args.planSelection — wizard plan-type ('collection' | 'settlement' | 'resolution' | false on first render)
 * @param {{ id: number }|undefined} args.payFrequency — the pay_frequency parent-dropdown row from redux
 * @param {string} args.payFrequencyValue — display name of the selected frequency (e.g. "Monthly")
 * @param {number|undefined} args.methodId — DDO id of the selected payment method
 * @param {Array<{ id: number, dropdown_id: number, name: string, code_key: string }|undefined>} args.dropdownOptions
 * @param {object} args.minPaymentByMethod — BE-supplied grid keyed [freq_code_key][method_code_key] → float
 * @returns {string|null} the min-payment formatted as `"x.xx"`, or null when not resolvable
 */
function lookupResolutionMinPayment({
  planSelection,
  payFrequency,
  payFrequencyValue,
  methodId,
  dropdownOptions,
  minPaymentByMethod,
}) {
  if (planSelection !== 'resolution') return null
  const freqDropdownId = payFrequency?.id
  if (!freqDropdownId || !payFrequencyValue) return null
  const freqCodeKey = dropdownOptions.find(
    (o) => o?.dropdown_id === freqDropdownId && o?.name === payFrequencyValue
  )?.code_key
  if (!methodId) return null
  const methodCodeKey = dropdownOptions.find((o) => o?.id === methodId)?.code_key
  if (!freqCodeKey || !methodCodeKey) return null
  const beMin = minPaymentByMethod?.[freqCodeKey]?.[methodCodeKey]
  return (typeof beMin === 'number') ? beMin.toFixed(2) : null
}

module.exports = { lookupResolutionMinPayment }
